import { useCallback, useEffect } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import '../globals.css';
import { useShallow } from 'zustand/shallow';
import { IconButton, Layout, Flex, Tooltip, icon as iconSize, iconWeight, space, Icon, useBreakpoint } from '@nula/core-ui';
import { Sidebar } from '@/layout/sidebar';
import { PanelLayout } from '@/layout/panel-layout';
import { WorkspaceView } from '@/app/routes/workspace/workspace-view';
import { SettingsModal } from '@/app/components/settings-modal';
import { UpdateNotifier } from '@/lib/updater/update-notifier';
import { useGlobalShortcut } from '@/hooks/use-global-shortcut';
import { useNativeMenuBridge } from '@/lib/platform/use-native-menu-bridge';
import { useTemplateRunPolling } from '@/lib/templates/use-template-run-polling';
import { useUIStore } from '@/stores/use-ui-store';
import { useIsMacOS } from '@/lib/platform/use-is-mac-os';
import { useIsWindows } from '@/lib/platform/use-is-windows';
import { WindowControls } from '@/layout/window-controls';
import { enableModernWindowStyle } from '@/lib/platform/mac-rounded-corners';
import { isDesktop } from '@/lib/platform';
import { vibrancyClass } from '@/app/vibrancy-class';
import { resolveAssistantMode } from '@/app/resolve-assistant-mode';
import { reconcileFullTabMode } from '@/app/reconcile-fulltab-mode';
import { QuickFind } from '@/features/search/components/quick-find';
import { StorageDegradedBanner } from '@/lib/sync/persistence/storage-degraded-banner';
import { PendingInvitationsBanner } from '@/lib/sharing/components/pending-invitations-banner';
import { RevocationModal } from '@/lib/sharing/components/revocation-modal';
import { useRevocationStore } from '@/lib/sharing/stores/use-revocation-store';
import { useActiveEntityId, useActiveCollectionId, useHasConversationTab } from '@/hooks/navigation';
import { useSessionStateOptional } from '@/session/session-context';
import { useCreateEntity } from '@/lib/workspace/hooks/use-create-entity';
import { useWorkspaceService } from '@/lib/workspace/use-workspace-service';
import { AppOverlays } from '@/layout/app-overlays';
import { WorkspaceAssistantChatProvider } from '@/features/assistant/components/assistant-chat-provider';
import { updateConversation } from '@/features/assistant/api/conversations';
import * as Sentry from '@sentry/react';
import { sessionOpenTab } from '@/session/session-bridge';
import { shortcutLabel } from '@/lib/platform/platform';
import styles from './app.module.css';

const topbarIconButton = cva(styles.topbarIconButton);
const centerRelative = cva(styles.centerRelative);

// _resetSplash is re-exported for tests that previously imported it from here.
import { _resetSplash } from '@/stores/use-boot-store';

export { _resetSplash };

export function App() {
  const session = useSessionStateOptional();
  const sessionState = session?.state ?? null;
  const focusedPanelId = sessionState?.focusedPanelId;
  const panel = focusedPanelId != null ? sessionState?.panels[focusedPanelId] : undefined;
  const activeId = panel?.activeId;
  const tabs = panel?.tabs ?? [];
  const docTabs = tabs.filter((t) => t.id !== 'home');
  const hasActiveTab = !!activeId && activeId !== 'home';

  const closeActiveTab = (hasActiveTab && session && focusedPanelId != null)
    ? () => session.closeTab(activeId!, focusedPanelId)
    : undefined;

  const activeDocIndex = docTabs.findIndex((t) => t.id === activeId);
  const navigateToPrevTab = (docTabs.length > 1 && session)
    ? () => {
        const prevIndex = (activeDocIndex - 1 + docTabs.length) % docTabs.length;
        session.openTab(docTabs[prevIndex].id);
      }
    : undefined;
  const navigateToNextTab = (docTabs.length > 1 && session)
    ? () => {
        const nextIndex = (activeDocIndex + 1) % docTabs.length;
        session.openTab(docTabs[nextIndex].id);
      }
    : undefined;

  const { createNewDocument, createNewCanvas, createNewCollection } = useCreateEntity();
  const workspaceService = useWorkspaceService();

  // Memoize the inline lambdas passed to Sidebar/PanelLayout so that, if those
  // components ever get wrapped in React.memo, prop identity stays stable
  // across App re-renders.
  const handleCreateDoc = useCallback(
    (type: 'document' | 'canvas') => {
      if (type === 'canvas') return void createNewCanvas();
      return void createNewDocument();
    },
    [createNewDocument, createNewCanvas],
  );
  const handleRenameDoc = useCallback(
    (id: string, title: string) => void workspaceService.renameDoc(id, title),
    [workspaceService],
  );
  const handleRenameCollection = useCallback(
    (id: string, name: string) => workspaceService.renameCollection(id, name),
    [workspaceService],
  );
  const handleRenameConversation = useCallback(
    // Conversations rename via REST PATCH; the server marks the title sticky
    // (title_generated=true) and emits a live UPSERTED event that updates the
    // entity store — so the tab re-renders with the new title automatically.
    (id: string, title: string) => {
      updateConversation(id, { title }).catch((err) => {
        Sentry.captureException(err, { extra: { action: 'rename-conversation', id } });
      });
    },
    [],
  );

  useGlobalShortcut({ closeActiveTab, navigateToPrevTab, navigateToNextTab, createNewDocument, createNewCanvas });
  useNativeMenuBridge();
  useTemplateRunPolling();
  const bp = useBreakpoint();
  const panelWidth = bp.md ? 'var(--drawer-width)' : '100vw';
  const isMacOS = useIsMacOS() ?? false;
  const isWindows = useIsWindows() ?? false;
  const vibrancy = vibrancyClass(isDesktop, isMacOS, isWindows);

  const activeEntityId = useActiveEntityId();
  const activeCollectionId = useActiveCollectionId();
  const setViewedResourceId = useRevocationStore((s) => s.setViewedResourceId);

  // Keep the revocation store's viewedResourceId in sync with the active panel.
  // entityEventHandler reads this to decide whether a DELETED event should
  // trigger the revocation modal.
  useEffect(() => {
    setViewedResourceId(activeEntityId ?? activeCollectionId);
  }, [activeEntityId, activeCollectionId, setViewedResourceId]);

  const {
    isSettingsOpen, settingsInitialTab, setSettingsOpen, isLeftSidebarOpen, toggleLeftSidebar,
    isZenMode, setZenMode,
    setAssistantMode,
  } = useUIStore(useShallow((s) => ({
    isSettingsOpen: s.isSettingsOpen, settingsInitialTab: s.settingsInitialTab, setSettingsOpen: s.setSettingsOpen,
    isLeftSidebarOpen: s.isLeftSidebarOpen, toggleLeftSidebar: s.toggleLeftSidebar,
    isZenMode: s.isZenMode, setZenMode: s.setZenMode,
    setAssistantMode: s.setAssistantMode,
  })));

  // Fade to App: the document viewer renders only when an entity is open.
  const isAppVisible = activeEntityId !== null;

  // The assistant bar is independent — it's available on every route, so it
  // surfaces (collapsed) regardless of whether an entity is open.
  useEffect(() => {
    const current = useUIStore.getState().assistantMode;
    const next = resolveAssistantMode(activeEntityId, current);
    if (next !== current) setAssistantMode(next);
  }, [activeEntityId, setAssistantMode]);

  // When the assistant's fullTab conversation is closed (by any means), the
  // mode would stay stuck at 'fullTab' with no tab to render. Reconcile back to
  // the floating bar so it reappears — same end state as the ⌘J reset.
  const hasConversationTab = useHasConversationTab();
  useEffect(() => {
    const current = useUIStore.getState().assistantMode;
    const next = reconcileFullTabMode(current, hasConversationTab);
    if (next !== current) setAssistantMode(next);
  }, [hasConversationTab, setAssistantMode]);

  // Keep --sidebar-w in sync so Portal-based overlays (ExpandableBar, AssistantOverlay)
  // stay centered over the content area rather than the full viewport.
  useEffect(() => {
    const w = isLeftSidebarOpen ? 256 : 80;
    document.documentElement.style.setProperty('--sidebar-w', `${w}px`);
  }, [isLeftSidebarOpen]);

  const handleNewChat = useCallback(() => {
    // Mint the conversationId at TAB CREATION. The tab id IS the conversationId
    // IS the chatId IS the corrId from the first render — one key, all layers.
    // No 'scratch' placeholder, no promotion. The DB row stays lazy server-side:
    // an opened-never-sent tab leaves no conversation_messages row (nexus only
    // ensure_exists at turn-complete).
    const conversationId = crypto.randomUUID();
    sessionOpenTab(conversationId, 'conversation');
    setAssistantMode('fullTab');
  }, [setAssistantMode]);

  const isQuickFindOpen = useUIStore((s) => s.isSearchOpen);
  const setQuickFindOpen = useUIStore((s) => s.setSearchOpen);

  // Initialize macOS rounded corners (App Store compatible)
  // Native traffic lights are hidden - WindowControls component provides custom controls
  useEffect(() => {
    if (isDesktop) {
      enableModernWindowStyle({ cornerRadius: 12 }).catch(console.error);
    }
  }, []);

  return (
    <WorkspaceAssistantChatProvider>
      <UpdateNotifier />
      <SettingsModal
        open={isSettingsOpen}
        initialTabId={settingsInitialTab ?? undefined}
        onClose={() => setSettingsOpen(false)}
      />

      <QuickFind open={isQuickFindOpen} onClose={() => setQuickFindOpen(false)} />

      <PendingInvitationsBanner />

      <RevocationModal />

      <div
        className={`nula-app${isZenMode ? ' zen-mode' : ''}${isAppVisible ? ' app-visible' : ''}${vibrancy ? ' ' + vibrancy : ''}`}
        data-left-sidebar={isLeftSidebarOpen ? 'open' : 'closed'}
      >
        {/* Left column — Sidebar */}
        <Layout.Sider
          width={256}
          collapsedWidth={64}
          collapsible
          collapsed={!isLeftSidebarOpen}
          onCollapse={() => toggleLeftSidebar()}
          trigger={null}
          className={clsx('nula-sidebar-left', styles.siderWidth)}
          data-slot="companion-wall-sidebar"
        >
          <Sidebar
            isOpen={isLeftSidebarOpen}
            onClose={toggleLeftSidebar}
            inline
            onSettingsClick={() => setSettingsOpen(true)}
            onOpenSubscription={(tabId) => setSettingsOpen(true, tabId)}
            onSearchClick={() => setQuickFindOpen(true)}
            showWindowControls={isDesktop && isMacOS}
            onCreateDoc={handleCreateDoc}
            onCreateCollection={createNewCollection}
            onNewChat={handleNewChat}
          />
        </Layout.Sider>

        {/* Center column */}
        <Flex vertical className={`nula-center ${centerRelative()}`}>
          {/* Storage degraded banner — shown when IndexedDB quota exceeded */}
          <StorageDegradedBanner />

          {/* Content area */}
          <Flex vertical className="nula-center-inner">
            <PanelLayout
              renderPanelContent={({ panelId, activeId, isFocused }) => (
                <WorkspaceView
                  panelId={panelId}
                  activeId={activeId}
                  isFocused={isFocused}
                />
              )}
              onRenameDoc={handleRenameDoc}
              onRenameCollection={handleRenameCollection}
              onRenameConversation={handleRenameConversation}
              extraTabActions={
              <Flex align="center" gap={space[2]} className={styles.tabBarActions}>
                <Tooltip title={[isZenMode ? 'Exit Focus' : 'Focus Mode', shortcutLabel('⌘\\', 'Ctrl+\\')].filter(Boolean).join(' · ')} placement="bottom">
                  <IconButton
                    size="sm"
                    onClick={() => setZenMode(!isZenMode)}
                    className={topbarIconButton()}
                  >
                    <Icon name={isZenMode ? 'fullscreen_exit' : 'fullscreen'} size={iconSize.md} weight={iconWeight.light} />
                  </IconButton>
                </Tooltip>
                {/* <NotificationPanel /> */}
                {isDesktop && !isMacOS && (
                  <Flex align="center" gap={space[1]} className={styles.windowControlsGroup}>
                    <WindowControls />
                  </Flex>
                )}
              </Flex>
            } />
          </Flex>
        </Flex>
      </div>

      <AppOverlays panelWidth={panelWidth} activeEntityId={activeEntityId} />
    </WorkspaceAssistantChatProvider>
  );
}
